/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import type { ReactElement } from 'react';
import { Icon } from '@/components/icon';
import { Badge } from '@/components/ui/badge';

export default function CartBadgeIcon({ numberOfItems }: { numberOfItems: number }): ReactElement {
    return (
        <>
            {/* No `size`: Figma's icon/lg is 24px, already the .material-symbols-sharp
                default. An inline font-size would opt this out of Button's size axis. */}
            <Icon name="shopping_cart" data-testid="shopping-cart-icon" />
            {numberOfItems > 0 && (
                <Badge
                    variant="neutral"
                    // A numeric COUNT BUBBLE, not a label badge: the circular
                    // `rounded-full` and the 18px box are deliberate and survive the
                    // radius-0 correction (CRCB2B-640), because Figma's badge spec
                    // covers short text labels, not overlay counters.
                    className="absolute -top-1 -right-1 flex h-[18px] min-w-[18px] items-center justify-center rounded-full px-1 text-xs"
                    data-testid="shopping-cart-badge">
                    {numberOfItems}
                </Badge>
            )}
        </>
    );
}
